import { getTokens } from 'core/classes/helpers/StorageHelper'
import settings from 'src/config/settings'

/** Attention statuses with reminders of their own, as the type names spell them. */
export type TCockpitReminderStatus =
  | 'Planned'
  | 'FinSobQuality'
  | 'InitiativesQuality'
  | 'Mdf'
  | 'SobActuals'
  | 'H2Review'
  | 'ProgramRisk'
  | 'QuarterPlan'
  | 'PendingActions'

/**
 * The reminders the service knows (`settings.reminder.types`); it keeps their templates and links.
 * `<recipient><Status>` is about one partner, `<recipient><Status>Partners` about a list of
 * partners with that status, `<recipient>Summary` about a list of partners with all their statuses.
 */
export type TCockpitReminderType =
  | `pbm${TCockpitReminderStatus}`
  | 'managerH2Review'
  | 'managerPendingActions'
  | `${'pbm' | 'manager'}${TCockpitReminderStatus}Partners`
  | 'pbmSummary'
  | 'managerSummary'

/** A partner of a list reminder, with the attention status bits the summary names. */
export interface ICockpitReminderPartner {
  partnerId: string
  statuses?: number[]
}

/** `partnerId` for a reminder about one partner, `partners` for one about a list. */
export interface ICockpitReminderContext {
  partnerId?: string
  partners?: ICockpitReminderPartner[]
  fYear: number
  programType?: string
}

export interface ICockpitReminderRequest {
  type: TCockpitReminderType
  to: { email: string; name: string }[]
  context: ICockpitReminderContext
}

export interface ICockpitReminderResult {
  email: string
  status: 'sent' | 'failed' | 'skipped'
  reason?: string
}

/** Whether a reminder can go to a recipient now (`reminder/check`); `skipped` carries the reason. */
export interface ICockpitReminderCheckResult {
  email: string
  status: 'ready' | 'skipped'
  reason?: string
}

interface ICockpitReminderResponse<TResult> {
  status: string
  data?: { results: TResult[] }
  message?: string
}

const postReminder = async <TResult>(
  action: 'send' | 'check',
  { type, to, context }: ICockpitReminderRequest
): Promise<TResult[]> => {
  const url = `${settings.httpsServices.partner360}/api/v0/reminder/${action}`
  const [accessToken] = getTokens()

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ type, to, context }),
  })

  const body = (await response.json().catch(() => null)) as ICockpitReminderResponse<TResult> | null

  if (!response.ok || body?.status !== 'success' || !body.data) {
    throw new Error(body?.message ?? `Reminder request failed with status ${response.status}`)
  }

  return body.data.results
}

/**
 * Sends a reminder of a type (`reminder/send`). The server picks the template and links of the type,
 * builds the links from the context, checks access to the partner and the recipients, and answers
 * per recipient.
 */
export const sendCockpitReminder = (request: ICockpitReminderRequest) =>
  postReminder<ICockpitReminderResult>('send', request)

/** Asks whether the same reminder would go out now (`reminder/check`), by the rules of the send; nothing is sent. */
export const checkCockpitReminder = (request: ICockpitReminderRequest) =>
  postReminder<ICockpitReminderCheckResult>('check', request)
